<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUiStore } from '../../stores/ui'
import { useVaultStore } from '../../stores/vault'
import MeCard from './MeCard.vue'
import PeopleList from './PeopleList.vue'
import PersonCard from './PersonCard.vue'
import Manage from './Manage.vue'
import RelMap from './RelMap.vue'
import Compose from './Compose.vue'
import Diary from './Diary.vue'
import NewPeoplePrompt from '../NewPeoplePrompt.vue'

const { t } = useI18n()
const ui = useUiStore()
const vault = useVaultStore()
const emit = defineEmits<{ toast: [msg: string] }>()

type View = 'map' | 'diary' | 'manage'
const view = ref<View>('map')
const mapMode = ref<'rel' | 'org'>('rel')
const current = ref(vault.people[0]?.id ?? '')
const writeText = ref('정사원이랑 윤대리 요즘 친해 보임')
const relMapRef = ref<{ grow: () => void; ping: (id: string) => void } | null>(null)
const composePrefill = ref('')
const manageFocus = ref<string | undefined>()
// 일기/기록에서 포착된 새 사람 후보(아직 등록 안 됨). 무시한 이름은 세션 동안 다시 안 묻는다.
const newPeople = ref<string[]>([])
const dismissed = new Set<string>()
function onNewPeople(names: string[]) { newPeople.value = names.filter(n => !dismissed.has(n)) }
async function addNewPerson(name: string) {
  const p = await vault.createPerson(name)
  relMapRef.value?.grow()
  newPeople.value = newPeople.value.filter(n => n !== name)
  manageFocus.value = p.id // 자동 전환 안 함. 관리 탭을 열면 이 사람으로 포커스
  emit('toast', t('newp.added', { name }))
}
function ignoreNewPeople() { newPeople.value.forEach(n => dismissed.add(n)); newPeople.value = [] }

function onUseThread(txt: string) {
  const p = vault.people.find(x => x.name && txt.includes(x.name))
  if (p) current.value = p.id
  composePrefill.value = txt
}
function onMapSelect(id: string | null) { if (id) current.value = id }
function onMapMe() { emit('toast', ui.stealth ? t('toast.saved') : `나 · 기록 ${vault.entries.length}개`) }
function onComposeGood(id: string) {
  relMapRef.value?.grow(); relMapRef.value?.ping(id) // 피드백도 성장·핑으로(지도가 떠 있으면)
  const p = vault.people.find(x => x.id === id)
  emit('toast', ui.stealth ? t('toast.saved') : `${p?.name}님 공략집에 기억해 둘게요`)
}

// 업무 모드에서 용어를 평범하게 바꾼다(data-s 스왑의 Vue판)
const term = (key: string) => (ui.stealth ? t(`${key}Work`) : t(key))

const navs = computed(() => [
  { v: 'map' as View, label: term('nav.map') },
  { v: 'diary' as View, label: term('nav.diary') },
  { v: 'manage' as View, label: term('nav.manage') },
])

async function record() {
  const txt = writeText.value.trim()
  if (!txt) return
  writeText.value = ''
  const { insight, foundIds, newNames } = await vault.logEntry(txt) // 암호화 저장 + 사람 태그·관계 발견
  relMapRef.value?.grow()
  for (const id of foundIds) relMapRef.value?.ping(id)
  emit('toast', ui.stealth ? t('toast.saved') : insight)
  onNewPeople(newNames)
}
function onDiaryGrow(foundIds: string[]) {
  relMapRef.value?.grow()
  for (const id of foundIds) relMapRef.value?.ping(id)
}
</script>

<template>
  <main class="grid">
    <!-- 왼쪽: 나 + 사람 목록 -->
    <aside class="col left">
      <MeCard />
      <h2>{{ term('left.people') }}</h2>
      <PeopleList :selected="current" @select="current = $event" />
    </aside>

    <!-- 가운데: 지도 / 일기 / 관리 -->
    <section class="col mid">
      <div class="view-tabs">
        <button v-for="n in navs" :key="n.v" :class="{ sel: view === n.v }" @click="view = n.v">{{ n.label }}</button>
      </div>

      <div v-if="view === 'map'">
        <div class="map-head">
          <div class="seg-toggle">
            <button :class="{ sel: mapMode === 'rel' }" @click="mapMode = 'rel'">{{ term('map.rel') }}</button>
            <button :class="{ sel: mapMode === 'org' }" @click="mapMode = 'org'">{{ t('map.org') }}</button>
          </div>
          <div class="legend">
            <span><i style="border-color:#3DBE8B"></i><b>{{ term('map.friend') }}</b></span>
            <span><i style="border-color:#E5484D;border-top-style:dashed"></i><b>{{ term('map.tension') }}</b></span>
            <span><i style="border-color:var(--pencil)"></i>{{ t('map.reportLine') }}</span>
          </div>
        </div>
        <div class="map-wrap"><RelMap ref="relMapRef" :mode="mapMode" :calm="!ui.motionActive" @select="onMapSelect" @me="onMapMe" /></div>
        <div class="write">
          <input v-model="writeText" :placeholder="term('map.writePlaceholder')" @keydown.enter="record" />
          <button @click="record">{{ t('map.writeButton') }}</button>
        </div>
        <div class="write-hint">{{ t('map.writeHint') }}</div>
      </div>

      <Diary v-else-if="view === 'diary'" @toast="emit('toast', $event)" @grow="onDiaryGrow" @use-thread="onUseThread" @new-people="onNewPeople" />
      <Manage v-else :focus-id="manageFocus" @toast="emit('toast', $event)" />
    </section>

    <!-- 오른쪽: 사람 카드 + 답장 -->
    <aside class="col right">
      <NewPeoplePrompt :names="newPeople" @add="addNewPerson" @ignore="ignoreNewPeople" />
      <PersonCard :person-id="current" />
      <Compose :person-id="current" :prefill="composePrefill" @toast="emit('toast', $event)" @good="onComposeGood" />
    </aside>
  </main>
</template>
